import {camelCase} from 'lodash';
import {FC, ReactNode} from 'react';
import {useBackend} from 'state/backend';
import {twMerge} from 'tailwind-merge';
import {t} from 'ttag';

import {
    PdfFilenameElementOption,
    PdfFilenameSectionName,
    PdfFilenameSettingsBackendData,
} from '../../types';

type FilenamePreviewProps = {
    elements: PdfFilenameElementOption[];
    hoverId: null | PdfFilenameElementOption;
    sectionName: PdfFilenameSectionName;
};

const ElementSpan: FC<{children: ReactNode; hovered: boolean}> = ({
    children,
    hovered = false,
}) => (
    <span
        className={twMerge('rounded hover:bg-gold-50', hovered && 'bg-gold-50')}
    >
        {children}
    </span>
);

const MAX_LENGTH = 251; // 255 - '.pdf'

const FilenamePreview: FC<FilenamePreviewProps> = ({
    elements,
    hoverId,
    sectionName,
}) => {
    const {
        data: {examples},
    } = useBackend<PdfFilenameSettingsBackendData>();

    let accumLength = 0;
    const exampleTexts = elements.map((element, index) => {
        const key = camelCase(element);
        const text = examples[sectionName][key];
        const startPosition = index > 0 ? accumLength + 1 : 0;
        const endPosition = startPosition + text.length;
        accumLength = endPosition;

        return {
            element,
            endPosition,
            startPosition,
            text,
        };
    });

    let truncatedPreview = exampleTexts.map(({text}) => text).join('_');

    if (truncatedPreview.length > MAX_LENGTH)
        truncatedPreview = `${truncatedPreview.slice(0, MAX_LENGTH - 3)}...`;

    return (
        <div>
            <span className="font-semibold">{t`Preview: `}</span>
            {exampleTexts.map(
                ({element, endPosition, startPosition}, index) => {
                    const hovered = hoverId === element;

                    const text = truncatedPreview.slice(
                        startPosition,
                        endPosition,
                    );

                    let separator = null;

                    if (index !== 0) {
                        const separatorCharacter =
                            truncatedPreview[startPosition - 1];

                        if (separatorCharacter === '_')
                            separator = <span>_</span>;
                        else if (separatorCharacter === '.')
                            separator = <span>.</span>;
                    }

                    return (
                        <span key={element}>
                            {separator}
                            {text !== '' && (
                                <ElementSpan hovered={hovered}>
                                    {text}
                                </ElementSpan>
                            )}
                        </span>
                    );
                },
            )}
            <span>.pdf</span>
        </div>
    );
};

export default FilenamePreview;
